import { useEffect, useState } from "react";
import { getMe, getMyMember } from "../pages/Access/api";

/**
 * useCurrentAccountMember() -> { accountId, accountMember, loading, error }
 *
 * One-shot fetch of the caller's own account-member row so pages can
 * gate CTAs on the coord-side role + delegated grants (see
 * helpers/appPermissions.js `canAddApplications`).
 *
 * Deliberately silent on error — the button gate treats a null member
 * as "no delegated grants known, hide the CTA". Any real network
 * failure surfaces via `error` for callers that want to render a
 * diagnostic; pages using the gate can ignore it.
 *
 * The coord PR that adds member.canCreateApplications is landing
 * separately, so /accounts/{id}/members/me may still return the row
 * without that field on older coord builds. That's fine: undefined ->
 * falsy -> button hidden -> helper text shown. See the task motivation
 * for the fallback contract.
 */
export function useCurrentAccountMember() {
  const [state, setState] = useState({
    accountId: null,
    accountMember: null,
    loading: true,
    error: null,
  });

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const self = await getMe();
        const accountId = self?.id ?? null;
        const accountMember = await getMyMember(accountId);
        if (cancelled) return;
        setState({
          accountId,
          accountMember: accountMember || null,
          loading: false,
          error: null,
        });
      } catch (e) {
        if (cancelled) return;
        setState({
          accountId: null,
          accountMember: null,
          loading: false,
          error: e,
        });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  return state;
}
